import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { AuthCard } from "@/components/AuthCard";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [f, setF] = useState({ password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (f.password.length < 8) return setError("Use at least 8 characters.");
    if (f.password !== f.confirm) return setError("The passwords don't match.");
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, password: f.password }); setDone(true); }
    catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  if (!token) {
    return (
      <AuthCard eyebrow="Password help" title="This link isn't complete." testId="reset-page">
        <p className="text-muted-foreground">Please use the button in your reset email, or <Link to="/forgot-password" className="text-pinkb hover:underline" data-testid="reset-request-new">request a new link</Link>.</p>
      </AuthCard>
    );
  }
  return (
    <AuthCard eyebrow="Password help" title={done ? "Password updated." : "Choose a new password."} testId="reset-page">
      {done ? (
        <div className="space-y-6" data-testid="reset-done">
          <p className="flex gap-3 text-sm"><CheckCircle2 className="h-5 w-5 text-leaf shrink-0" />You can now log in with your new password.</p>
          <Button asChild className="w-full h-11 rounded-full bg-navy hover:bg-navy/90 text-white"><Link to="/login" data-testid="reset-login-btn">Log in</Link></Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
          <Input type="password" autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="New password (at least 8 characters)" data-testid="reset-password-input" className="h-11 bg-white" />
          <Input type="password" autoComplete="new-password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} placeholder="New password again" data-testid="reset-confirm-input" className="h-11 bg-white" />
          {error && <p className="text-sm text-rose-600" data-testid="reset-error">{error} {error.includes("expired") && <Link to="/forgot-password" className="text-pinkb hover:underline">Request a new link</Link>}</p>}
          <Button type="submit" disabled={busy} data-testid="reset-submit-btn" className="w-full h-11 rounded-full bg-navy hover:bg-navy/90 text-white">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save new password
          </Button>
        </form>
      )}
    </AuthCard>
  );
}
